import { createApi } from '@reduxjs/toolkit/query/react';

import { serializeFilterParams } from '../../utils/api-params';
import { DemandReadiness } from '../types/case-context-types';
import {
  BaseMatter,
  CreateMatterRequest,
  DetailedMatter,
  MatterKeyConsideration,
  MatterSummary,
  MatterUpdate,
} from '../types/client-intake-types';
import { CompanionsContextResponse } from '../types/companions-types';
import {
  DocumentInstanceDto,
  LorEmailMetadataPayload,
} from '../types/document-types';
import { FormField } from '../types/form-types';
import { MatterClient } from '../types/matter-client-types';
import { RecordsRequest } from '../types/matter-records-request';
import { MatterListItem, MatterListMetadata } from '../types/matter-types';
import {
  MatterFilterParams,
  PaginatedResponse,
} from '../types/pagination-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import { coverageApiClient } from './coverageService';
import { tasksApiClient } from './tasksService';

/** Response type for a single workflow in matter services endpoint */
export interface MatterServiceWorkflowResponse {
  id: string | null;
  matter_service_id: string;
  workflow_code: string;
  display_name: string;
  description: string | null;
  status: string;
  is_enabled: boolean;
  source: string;
  notes: string;
  config: Record<string, unknown> | null;
  config_fields: unknown[];
  task_types: string[];
}

/** Response type for workflow guidelines in task detail view */
export interface WorkflowGuidelinesResponse {
  workflow_code: string;
  workflow_display_name: string;
  firm_notes: string | null;
  matter_notes: string | null;
  firm_config_summary: string | null;
}

/** Response type for the nested service object */
export interface ServiceResponse {
  id: string;
  name: string;
  display_name: string;
  description: string;
}

/** Response type for matter services endpoint - matches backend MatterServiceWithWorkflowsSchema */
export interface MatterServiceWithWorkflowsResponse {
  id: string;
  matter_id: string;
  service_id: string;
  service: ServiceResponse;
  notes: string;
  is_active: boolean;
  workflows: MatterServiceWorkflowResponse[];
}

export const mattersApiClient = createApi({
  reducerPath: 'matterApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'Matter',
    'RecordsRequest',
    'Matters',
    'MatterDocuments',
    'MatterServices',
  ] as const,
  endpoints: (builder) => ({
    // Companions context for a matter
    getMatterCompanions: builder.query<CompanionsContextResponse, string>({
      query: (matterId) => `api/matters/${matterId}/companions/`,
      providesTags: (result, error, matterId) => [
        { type: 'Matter', id: matterId },
      ],
    }),
    patchMatterCompanions: builder.mutation<
      CompanionsContextResponse,
      { matter_id: string; make_primary?: boolean }
    >({
      query: ({ matter_id, make_primary }) => ({
        url: `api/matters/${matter_id}/companions/`,
        method: 'PATCH',
        body: { make_primary: !!make_primary },
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
        'Matters', // Invalidate matters list to refresh dropdown options
      ],
    }),
    patchMatterCompanionMembers: builder.mutation<
      CompanionsContextResponse,
      { matter_id: string; add?: string[]; remove?: string[] }
    >({
      query: ({ matter_id, add, remove }) => ({
        url: `api/matters/${matter_id}/companions/members/`,
        method: 'PATCH',
        body: { add: add ?? [], remove: remove ?? [] },
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
        'Matters', // Invalidate matters list to refresh dropdown options
      ],
      async onQueryStarted({ matter_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            coverageApiClient.util.invalidateTags([
              { type: 'CompanionCoverages', id: matter_id },
            ]),
          );
        } catch {
          // no-op
        }
      },
    }),
    // No DELETE endpoint; use patchMatterCompanionMembers to clear primary when singleton
    getMatterDetails: builder.query<DetailedMatter, string>({
      query: (matterId) => `api/matters/case_details/${matterId}/`,
      transformResponse: (response: DetailedMatter): DetailedMatter => ({
        ...response,
        summaries: response.summaries.sort((a, b) =>
          a.category.localeCompare(b.category),
        ),
      }),
      providesTags: (result, error, matterId) => [
        { type: 'Matter', id: matterId },
      ],
    }),

    getMatter: builder.query<DetailedMatter, string>({
      query: (matterId) => `api/matters/${matterId}/`,
      providesTags: (result, error, matterId) => [
        { type: 'Matter', id: matterId },
      ],
    }),
    getMatters: builder.query<
      { results: BaseMatter[]; totalCount: number },
      {
        firm?: string[];
        status?: string[];
        search?: string;
        limit?: number;
        case_manager?: string[];
        representing_attorney?: string[];
        matter_type?: string[];
      } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        // Default limit for dropdown/selector use cases
        // Backend now always returns paginated response
        const limit = params?.limit ?? 50;
        searchParams.append('limit', String(limit));

        if (params?.firm && params.firm.length > 0) {
          // Concatenate array items as a comma-separated string
          searchParams.append('firm', params.firm.join(','));
        }

        if (params?.status && params.status.length > 0) {
          // Concatenate array items as a comma-separated string
          searchParams.append('status', params.status.join(','));
        }

        if (params?.search) {
          searchParams.append('search', params.search);
        }

        if (params?.case_manager && params.case_manager.length > 0) {
          searchParams.append('case_manager', params.case_manager.join(','));
        }

        if (
          params?.representing_attorney &&
          params.representing_attorney.length > 0
        ) {
          searchParams.append(
            'representing_attorney',
            params.representing_attorney.join(','),
          );
        }

        if (params?.matter_type && params.matter_type.length > 0) {
          searchParams.append(
            'matter_type',
            params.matter_type.join(','),
          );
        }

        return `api/matters/?${searchParams.toString()}`;
      },
      transformResponse: (
        response: PaginatedResponse<BaseMatter> | BaseMatter[],
      ): { results: BaseMatter[]; totalCount: number } => {
        // Handle paginated response from backend (new behavior)
        if ('results' in response) {
          return { results: response.results, totalCount: response.count };
        }
        // Fallback for backwards compatibility
        return { results: response, totalCount: response.length };
      },
      providesTags: ['Matters'],
    }),
    /** Get paginated list of matters with filtering and sorting support */
    getMatterList: builder.query<
      PaginatedResponse<MatterListItem>,
      MatterFilterParams
    >({
      query: (params) => ({
        url: '/api/matters/',
        params: serializeFilterParams(params),
      }),
      providesTags: ['Matters'],
    }),
    /** Get metadata for matters list (status counts, filter options) */
    getMatterListMetadata: builder.query<MatterListMetadata, void>({
      query: () => '/api/matters/metadata/',
      providesTags: ['Matters'],
    }),
    // New: list documents for a matter
    getMatterDocuments: builder.query<DocumentInstanceDto[], string>({
      query: (matterId) => `api/matters/${matterId}/documents/`,
      providesTags: (result, error, matterId) => [
        { type: 'MatterDocuments', id: matterId },
      ],
    }),
    getMatterDocument: builder.query<
      DocumentInstanceDto,
      { matterId: string; documentId: string }
    >({
      query: ({ matterId, documentId }) =>
        `api/matters/${matterId}/documents/${documentId}/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'MatterDocuments', id: matterId },
      ],
    }),
    // New: fetch preview as blob
    getMatterDocumentPreview: builder.query<
      { blob: Blob; filename: string },
      { matter_id: string; document_id: string; t?: number }
    >({
      query: ({ matter_id, document_id, t }) => ({
        url: `api/matters/${matter_id}/documents/${document_id}/preview/${
          t ? `?t=${encodeURIComponent(String(t))}` : ''
        }`,
        method: 'GET',
        cache: 'no-store',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const cd =
            response.headers.get('Content-Disposition') ||
            response.headers.get('content-disposition') ||
            '';
          const match = cd.match(/filename="?([^";]+)"?/i) ?? [];
          const [, parsedName] = match as string[];
          const filename = parsedName || 'Letter-of-Representation.pdf';
          return { blob, filename };
        },
      }),
      providesTags: (result, error, { matter_id }) => [
        { type: 'MatterDocuments', id: matter_id },
      ],
    }),
    resendMatterDocument: builder.mutation<
      DocumentInstanceDto,
      { matter_id: string; document_id: string }
    >({
      query: ({ matter_id, document_id }) => ({
        url: `api/matters/${matter_id}/documents/${document_id}/resend/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'MatterDocuments', id: matter_id },
      ],
    }),
    setLorEmailMetadata: builder.mutation<
      DocumentInstanceDto,
      {
        matterId: string;
        documentId: string;
        payload: LorEmailMetadataPayload;
      }
    >({
      query: ({ matterId, documentId, payload }) => ({
        url: `api/matters/${matterId}/documents/${documentId}/lor-email/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MatterDocuments', id: matterId },
      ],
    }),
    createMatter: builder.mutation<
      { matter: DetailedMatter },
      CreateMatterRequest
    >({
      query: (body) => ({
        url: `api/matters/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Matters'],
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate tasks cache since new matters affect filter options
          dispatch(
            tasksApiClient.util.invalidateTags(['Tasks', 'TaskListMetadata']),
          );
        } catch {
          // Ignore errors - matter creation failed
        }
      },
    }),
    patchMatter: builder.mutation<
      MatterUpdate,
      { matter_id: string; patch: MatterUpdate }
    >({
      query: ({ matter_id, patch }) => ({
        url: `api/matters/${matter_id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matters' },
        { type: 'Matter', id: matter_id },
      ],
      async onQueryStarted({ matter_id, patch }, { dispatch, queryFulfilled }) {
        // Update the cache optimistically
        const patchResult = dispatch(
          mattersApiClient.util.updateQueryData(
            'getMatter',
            matter_id,
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );
        const patchDetailsResult = dispatch(
          mattersApiClient.util.updateQueryData(
            'getMatterDetails',
            matter_id,
            (draft) => {
              Object.assign(draft, patch);
            },
          ),
        );
        try {
          await queryFulfilled;
          // After successful update, invalidate tasks cache (cross-API invalidation)
          // since tasks display matter status, incident type, etc.
          dispatch(
            tasksApiClient.util.invalidateTags([
              'Tasks',
              'TaskCounts',
              'TaskListMetadata',
            ]),
          );
        } catch {
          // Revert the optimistic update on error
          patchResult.undo();
          patchDetailsResult.undo();
        }
      },
    }),
    markRetainerSigned: builder.mutation<DetailedMatter, { matter_id: string }>(
      {
        query: ({ matter_id }) => ({
          url: `api/matters/${matter_id}/retainer/mark-signed/`,
          method: 'POST',
        }),
        async onQueryStarted({ matter_id }, { dispatch, queryFulfilled }) {
          // Optimistically set retainer_signed to true
          const patchMatterResult = dispatch(
            mattersApiClient.util.updateQueryData(
              'getMatter',
              matter_id,
              (draft) => {
                (draft as any).retainer_signed = true;
              },
            ),
          );
          const patchDetailsResult = dispatch(
            mattersApiClient.util.updateQueryData(
              'getMatterDetails',
              matter_id,
              (draft) => {
                (draft as any).retainer_signed = true;
              },
            ),
          );
          try {
            await queryFulfilled;
            // Delay re-pull to avoid flicker back to unsigned while backend events settle
            setTimeout(() => {
              dispatch(
                mattersApiClient.util.invalidateTags([
                  { type: 'Matter', id: matter_id } as any,
                  'Matters' as any,
                ]),
              );
            }, 1000);
          } catch {
            patchMatterResult.undo();
            patchDetailsResult.undo();
          }
        },
      },
    ),
    updateFormField: builder.mutation<
      FormField,
      { matter_id: string; id: string; patch: Partial<FormField> }
    >({
      query: ({ matter_id, id, patch }) => ({
        url: `api/matters/${matter_id}/formfields/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),

    // Matter Key Considerations
    createMatterKeyConsideration: builder.mutation<
      MatterKeyConsideration,
      { matter_id: string; body: Omit<MatterKeyConsideration, 'id'> }
    >({
      query: ({ matter_id, body }) => ({
        url: `api/matters/${matter_id}/key_considerations/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),
    patchMatterKeyConsideration: builder.mutation<
      MatterKeyConsideration,
      { matter_id: string; id: string; patch: Partial<MatterKeyConsideration> }
    >({
      query: ({ matter_id, id, patch }) => ({
        url: `api/matters/${matter_id}/key_considerations/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),
    deleteMatterKeyConsideration: builder.mutation<
      void,
      { matter_id: string; id: string }
    >({
      query: ({ matter_id, id }) => ({
        url: `api/matters/${matter_id}/key_considerations/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),

    // Matter Summaries

    createMatterSummary: builder.mutation<
      MatterSummary,
      { matter_id: string; body: MatterSummary }
    >({
      query: ({ matter_id, body }) => ({
        url: `api/matters/${matter_id}/summaries/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),

    patchMatterSummary: builder.mutation<
      MatterSummary,
      { matter_id: string; id: string; patch: Partial<MatterSummary> }
    >({
      query: ({ matter_id, id, patch }) => ({
        url: `api/matters/${matter_id}/summaries/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),
    deleteMatterSummary: builder.mutation<
      void,
      { matter_id: string; id: string }
    >({
      query: ({ matter_id, id }) => ({
        url: `api/matters/${matter_id}/summaries/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
      ],
    }),
    patchMatterClient: builder.mutation<
      MatterClient,
      {
        matter_id: string;
        matter_client_id: string;
        patch: Partial<MatterClient>;
      }
    >({
      query: ({ matter_id, matter_client_id, patch }) => ({
        url: `api/matters/${matter_id}/client/${matter_client_id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'Matter', id: matter_id },
        'Matters', // Also invalidate the matters list to refresh the table
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate tasks cache since they may display client info
          dispatch(
            tasksApiClient.util.invalidateTags(['Tasks', 'TaskListMetadata']),
          );
        } catch {
          // Ignore errors - update failed
        }
      },
    }),
    getMatterMedicalRecordsRequest: builder.query<
      RecordsRequest | null,
      { matter_id: string }
    >({
      query: ({ matter_id }) =>
        `api/matters/${matter_id}/records_request_alpha/`,
      transformResponse: (response: RecordsRequest, meta) => {
        if (meta?.response?.status === 204) {
          return null;
        }
        return response;
      },
      providesTags: (result, error, { matter_id }) => [
        { type: 'RecordsRequest', id: matter_id },
      ],
    }),
    createMatterMedicalRecordsRequest: builder.mutation<
      RecordsRequest,
      { matter_id: string; body: Pick<RecordsRequest, 'request'> }
    >({
      query: ({ matter_id, body }) => ({
        url: `api/matters/${matter_id}/records_request_alpha/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matter_id }) => [
        { type: 'RecordsRequest', id: matter_id },
      ],
    }),
    /** Get services with workflows for a matter */
    getMatterServices: builder.query<
      MatterServiceWithWorkflowsResponse[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/services/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'MatterServices', id: matterId },
      ],
    }),
    /** Create a new matter service (auto-copies workflows from firm defaults) */
    createMatterService: builder.mutation<
      MatterServiceWithWorkflowsResponse,
      {
        matterId: string;
        serviceId: string;
      }
    >({
      query: ({ matterId, serviceId }) => ({
        url: `api/matters/${matterId}/services/`,
        method: 'POST',
        body: { service_id: serviceId },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MatterServices', id: matterId },
      ],
    }),
    /** Update a matter service (is_active, notes) */
    patchMatterService: builder.mutation<
      MatterServiceWithWorkflowsResponse,
      {
        matterId: string;
        matterServiceId: string;
        patch: { is_active?: boolean; notes?: string };
      }
    >({
      query: ({ matterId, matterServiceId, patch }) => ({
        url: `api/matters/${matterId}/services/${matterServiceId}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MatterServices', id: matterId },
      ],
    }),
    /** Bulk update workflows for a matter service */
    bulkUpdateMatterServiceWorkflows: builder.mutation<
      unknown,
      {
        matterId: string;
        matterServiceId: string;
        workflows: {
          workflow_code: string;
          is_enabled: boolean;
          notes: string;
        }[];
      }
    >({
      query: ({ matterId, matterServiceId, workflows }) => ({
        url: `api/matters/${matterId}/services/${matterServiceId}/workflows/bulk`,
        method: 'POST',
        body: { workflows },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MatterServices', id: matterId },
      ],
    }),
    /** Sync matter services from firm defaults (reset) */
    syncMatterServicesFromFirm: builder.mutation<
      {
        matter_id: string;
        services_created: number;
        services_activated: number;
        services_deactivated: number;
        workflows_created: number;
        workflows_enabled: number;
        workflows_disabled: number;
      },
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/services/sync-from-firm/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'MatterServices', id: matterId },
        { type: 'Matter', id: matterId },
      ],
    }),
    /** Get workflow guidelines for a task type on a matter */
    getTaskGuidelines: builder.query<
      WorkflowGuidelinesResponse[],
      { matterId: string; taskType: string }
    >({
      query: ({ matterId, taskType }) => ({
        url: `api/matters/${matterId}/task-guidelines/`,
        params: { task_type: taskType },
      }),
      // No tags needed - this is task-context data that shouldn't trigger refetches
    }),
    /** Save demand readiness information when transitioning to Ready for Demand */
    saveDemandReadiness: builder.mutation<
      { demand_readiness: DemandReadiness },
      { matterId: string; demandReadiness: DemandReadiness }
    >({
      query: ({ matterId, demandReadiness }) => ({
        url: `api/matters/${matterId}/demand-readiness/`,
        method: 'POST',
        body: demandReadiness,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Matter', id: matterId },
      ],
    }),
    /** Get demand readiness information for a matter */
    getDemandReadiness: builder.query<
      { demand_readiness: DemandReadiness | null },
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/demand-readiness/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'Matter', id: matterId },
      ],
    }),
    /** List all meetings for a matter */
    listMeetings: builder.query<
      {
        id: string;
        scheduled_at: string | null;
        scheduled_by_id: string | null;
        scheduled_by_name: string | null;
        attendee_ids: string[];
        attendee_names: string[];
        notes: string | null;
        google_calendar_event_id: string | null;
        google_calendar_event_link: string | null;
        cancelled_at: string | null;
        created_at: string | null;
      }[],
      string
    >({
      query: (matterId) => `api/matters/${matterId}/meetings/`,
      providesTags: (_, __, matterId) => [
        { type: 'Matter', id: `meetings-${matterId}` },
      ],
    }),
    /** Schedule a new meeting for a matter */
    scheduleMeeting: builder.mutation<
      { status: string },
      {
        matterId: string;
        scheduled_at: string;
        attendee_ids: string[];
        notes?: string;
        skip_calendar?: boolean;
        timezone?: string;
      }
    >({
      query: ({ matterId, ...body }) => ({
        url: `api/matters/${matterId}/schedule-meeting/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { matterId }) => [
        { type: 'Matter', id: `meetings-${matterId}` },
      ],
    }),
    /** Cancel a specific meeting */
    cancelMeeting: builder.mutation<
      { status: string },
      { matterId: string; meetingId: string }
    >({
      query: ({ matterId, meetingId }) => ({
        url: `api/matters/${matterId}/cancel-meeting/${meetingId}/`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { matterId }) => [
        { type: 'Matter', id: `meetings-${matterId}` },
      ],
    }),
    /** Get free 30-minute calendar slots for the current user */
    getFreeSlots: builder.query<
      { start_time: string; end_time: string }[],
      { matterId: string; date: string; timezone?: string }
    >({
      query: ({ matterId, date, timezone }) => {
        const params = new URLSearchParams({ date });
        if (timezone) params.set('timezone', timezone);
        return `api/matters/${matterId}/free-slots/?${params.toString()}`;
      },
    }),
    /** Escalate a case to the Managing Paralegal */
    escalateCase: builder.mutation<
      {
        id: string;
        matter_id: string;
        name: string;
        description: string;
        assignee_id: string | null;
        due_date: string | null;
      },
      { matterId: string; description: string }
    >({
      query: ({ matterId, description }) => ({
        url: `api/matters/${matterId}/escalate/`,
        method: 'POST',
        body: { description },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate tasks cache so the new escalation task appears
          dispatch(tasksApiClient.util.invalidateTags(['Tasks']));
        } catch {
          // Ignore errors - escalation failed
        }
      },
    }),
  }),
});

export const {
  useGetMatterDetailsQuery,
  useGetMatterCompanionsQuery,
  usePatchMatterCompanionsMutation,
  usePatchMatterCompanionMembersMutation,
  useUpdateFormFieldMutation,
  usePatchMatterMutation,
  useMarkRetainerSignedMutation,
  useCreateMatterMutation,
  useCreateMatterKeyConsiderationMutation,
  usePatchMatterKeyConsiderationMutation,
  useDeleteMatterKeyConsiderationMutation,
  useCreateMatterSummaryMutation,
  usePatchMatterSummaryMutation,
  useDeleteMatterSummaryMutation,
  usePatchMatterClientMutation,
  useCreateMatterMedicalRecordsRequestMutation,
  useGetMatterMedicalRecordsRequestQuery,
  useGetMattersQuery,
  useGetMatterQuery,
  useGetMatterDocumentsQuery,
  useGetMatterDocumentQuery,
  useLazyGetMatterDocumentsQuery,
  useGetMatterDocumentPreviewQuery,
  useResendMatterDocumentMutation,
  useSetLorEmailMetadataMutation,
  useGetMatterListQuery,
  useGetMatterListMetadataQuery,
  useGetMatterServicesQuery,
  useCreateMatterServiceMutation,
  usePatchMatterServiceMutation,
  useBulkUpdateMatterServiceWorkflowsMutation,
  useSyncMatterServicesFromFirmMutation,
  useGetTaskGuidelinesQuery,
  useSaveDemandReadinessMutation,
  useGetDemandReadinessQuery,
  useEscalateCaseMutation,
  useListMeetingsQuery,
  useScheduleMeetingMutation,
  useCancelMeetingMutation,
  useGetFreeSlotsQuery,
} = mattersApiClient;
